import { I18n } from '@aws-amplify/core';
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components';
import { Container } from '@chakra-ui/layout';
import { Spinner } from '@chakra-ui/spinner';
import { Auth } from 'aws-amplify';
import { AxiosResponse } from 'axios';
import { routerActions } from 'connected-react-router';
import React, { Consumer, lazy, useEffect, useLayoutEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import './App.scss';
import { useAppDispatch, useAppSelector } from './hooks';
import { COGNITO_TRANSLATIONS } from './i18';
import { Shell } from './pages/shell';
import { isLoading, stopLoading } from './slice/loadingSlice';
import { CognitoPayload, signIn, signOutAsync } from './slice/userSlice';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { LOGGER_MESSAGES } from './constants/message';
import useEventLogger from './hooks/useEventLogger/useEventLogger';
const Top = lazy(() => import('./pages/top/index'));
const SunNotice = lazy(() => import('./pages/sun_notice/index'));
const Group = lazy(() => import('./pages/group/index'));
const Groups = lazy(() => import('./pages/groups/index'));
const Learner = lazy(() => import('./pages/learner/index'));
const Learners = lazy(() => import('./pages/learners/index'));
const LessonFrame = lazy(() => import('./pages/lesson_frame/index'));
const Lessons = lazy(() => import('./pages/lessons/index'));
const Notice = lazy(() => import('./pages/notice/index'));
const Notices = lazy(() => import('./pages/notices/index'));
const OrganizationUsers = lazy(() => import('./pages/organizationusers/index'));
const Reports = lazy(() => import('./pages/reports/index'));
const Archives = lazy(() => import('./pages/archives/index'));
const Settings = lazy(() => import('./pages/settings/index'));
const Signin = lazy(() => import('./pages/signin/index'));
const Teacher = lazy(() => import('./pages/teacher/index'));
const Teachers = lazy(() => import('./pages/teachers/index'));
// export let orgKey = React.createContext('');
I18n.putVocabulariesForLanguage('ja', COGNITO_TRANSLATIONS);
const PrivateRoute = ({ render, userState, ...rest }: any) => {
  return (
    <Route
      {...rest}
      render={
        userState.authState === AuthState.SignedIn && userState.user
          ? render
          : () => <Redirect to="/signin" />
      }
    />
  );
};
export const Loading = () => (
  <Container className="App-Loading">
    <Spinner />
  </Container>
);

const extractCognitoPayload = (authenticatedUser: any): CognitoPayload => {
  try {
    return {
      'cognito:username': authenticatedUser.signInUserSession.idToken.payload['cognito:username'],
      'cognito:organizationKey': authenticatedUser.signInUserSession.idToken.payload['cognito:organizationKey'],
      email: authenticatedUser.signInUserSession.idToken.payload.email,
      exp: authenticatedUser.signInUserSession.idToken.payload.exp,
    };
  } catch (error) {
    throw error;
  }
};

const App = () => {
  const userState = useAppSelector((state) => state.user);
  const isloading = useAppSelector(isLoading);
  const dispatch = useAppDispatch();
  const { sendInfoLog } = useEventLogger();
  const { listen } = useHistory()
  
  useLayoutEffect(() => { 
    listen((event) => { 
      console.log(event)
      sendInfoLog({
        message: LOGGER_MESSAGES.PV,
        payload: {
          event
        }
      })
    })  
    return () => {
      listen(() =>{ })
    }
  }, [])
    
  useEffect(() => {
    const init = async () => {
      const authenticatedUser = await Auth.currentAuthenticatedUser().catch((e) => null);
      if (authenticatedUser) {
        const expayload = extractCognitoPayload(authenticatedUser);
        dispatch(
          signIn({
            authState: AuthState.SignedIn,
            user: expayload,
          }),
        );
        dispatch(stopLoading());
        // orgKey = React.createContext(authenticatedUser.attributes['custom:organizationKey']);
      } else {
        await dispatch(signOutAsync());
        dispatch(stopLoading());
        dispatch(routerActions.push('/signin'));
      }
    };
    init();
    return onAuthUIStateChange((nextAuthState, authenticatedUser) => {
      if (nextAuthState === AuthState.SignedIn && authenticatedUser) {
        dispatch(
          signIn({
            authState: AuthState.SignedIn,
            user: extractCognitoPayload(authenticatedUser),
          }),
        );
      }
    });
  }, [dispatch]);
  return isloading ? (
    <Loading />
  ) : (
    <Switch>
      <React.Suspense fallback={<Loading />}>
        <Shell>
          <Route
            exact
            path="/"
            render={() => {
              return userState.authState === AuthState.SignedIn && userState.user ? (
                <Redirect to="/top" />
              ) : (
                <Redirect to="/signin" />
              );
            }}
          />
          <Route
            exact
            path="/signin"
            render={() => {
              return userState.authState === AuthState.SignedIn && userState.user ? (
                <Redirect to="/top" />
              ) : (
                <Signin />
              );
            }}
          />
          <PrivateRoute exact path="/top" render={() => <Top />} userState={userState} />
          <PrivateRoute
            exact
            path="/sun_notices/:noticeId"
            render={() => <SunNotice />}
            userState={userState}
          />
          <PrivateRoute exact path="/groups" render={() => <Groups />} userState={userState} />
          <PrivateRoute
            exact
            path="/groups/:groupId"
            render={() => <Group />}
            userState={userState}
          />
          <PrivateRoute exact path="/learners" render={() => <Learners />} userState={userState} />
          <PrivateRoute
            exact
            path="/learners/:learnerId"
            render={() => <Learner />}
            userState={userState}
          />
          <PrivateRoute exact path="/lessons" render={() => <Lessons />} userState={userState} />
          <PrivateRoute
            exact
            path="/lesson_frame/:lessonFrameId"
            render={() => <LessonFrame />}
            userState={userState}
          />
          <PrivateRoute exact path="/notices" render={() => <Notices />} userState={userState} />
          <PrivateRoute
            exact
            path="/notices/:noticeId"
            render={() => <Notice />}
            userState={userState}
          />
          <PrivateRoute exact path="/organizationusers" render={() => <OrganizationUsers />} userState={userState} />
          <PrivateRoute exact path="/reports" render={() => <Reports />} userState={userState} />
          <PrivateRoute exact path="/archives" render={() => <Archives />} userState={userState} />
          <PrivateRoute exact path="/settings" render={() => <Settings />} userState={userState} />
          <PrivateRoute exact path="/teachers" render={() => <Teachers />} userState={userState} />
          <PrivateRoute
            exact
            path="/teachers/:teacherId"
            component={Teacher}
            userState={userState}
          />
        </Shell>
      </React.Suspense>
    </Switch>
  );
};
export default App;

export const reloadIfTokenExpired = (e : any) => {
  const rtn :AxiosResponse = {
    data: [] as never,
    status: 0,
    statusText: '',
    headers: '' as never,
    config: '' as never
  };
  if(e.message.match(/(401)/)){
    window.location.reload();
    return rtn;
  }
    return rtn;
}

export const exchangeTimeZone = (datetime: Date) => {
  let today = datetime;
  if(typeof(datetime) == 'string'){
    today = new Date(today);
  }
  // 出力用にフォーマット(入力値をそのまま出力)
  let fromattedTime = today.getUTCFullYear() + '/' + (today.getUTCMonth() + 1).toString().padStart(2, '0') + '/' + today.getUTCDate().toString().padStart(2, '0') + ' ' + today.getUTCHours().toString().padStart(2, '0') + ':' + today.getUTCMinutes().toString().padStart(2, '0') + ':' + today.getUTCSeconds().toString().padStart(2, '0');
  return fromattedTime;
}
export const exchangeTimeZone2 = (datetime: Date) => {
  let today = datetime;
  if(typeof(datetime) == 'string'){
    today = new Date(today);
  }
  // 出力用にフォーマット(入力値をそのまま出力)
  let fromattedTime = today.getFullYear() + '/' + (today.getMonth() + 1).toString().padStart(2, '0') + '/' + today.getDate().toString().padStart(2, '0') + ' ' + today.getHours().toString().padStart(2, '0') + ':' + today.getMinutes().toString().padStart(2, '0') + ':' + today.getSeconds().toString().padStart(2, '0');
  return fromattedTime;
}
export const formatYMD = (YMD: string) => {
  return `${YMD.substring(0,4)}/${YMD.substring(4,6)}/${YMD.substring(6,8)}`;
}
